import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Grid, Stack, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';

import {
  trackClickStructuredGenerateAll,
  trackClickStructuredGenerateAllCancel,
  trackClickStructuredGenerateAllConfirm,
  trackClickStructuredMoreIdeas,
  trackClickStructuredMoreIdeasCancel,
  trackClickStructuredMoreIdeasConfirm,
} from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import { generateMoreIdeas, handleApiError, revealStructuredItems } from '../utils/api';
import { colors } from '../utils/colors';
import { DEFAULT_GENERATION_COST } from '../utils/const';
import { useListenStructuredItems } from '../utils/db';
import { useFirebaseLoginState } from '../utils/hooks';
import { useLegacyFacebookItems } from '../hooks/useLegacyFacebookItems';
import { RootState } from '../utils/store';
import {
  Item,
  LoadingStatus,
  StructuredItem,
  SectionName,
  TabName,
} from '../utils/types';
import { GeneratedPost } from './GeneratedPost';
import { IdeaCard } from './IdeaCard';
import { LegacyPreviewBanner } from './banners/LegacyPreviewBanner';
import { GenerationInProgressMessage } from './minicomponents/GenerationInProgressMessage';
import { GradientButton } from './minicomponents/GradientButton';
import { GradientCircularProgress } from './minicomponents/GradientCircularProgress';
import { IconGenerateMore, IconRocketLaunchFilled } from './minicomponents/icons';
import AlertPopup from './popups/AlertPopup';
import ConfirmationPopup from './popups/ConfirmationPopup';
import LegacyCampaignPreviewPopup from './popups/LegacyCampaignPreviewPopup';
import UpgradePopup from './popups/UpgradePopup';

const additionalIdeasCount = 3; // This should align with ADDITIONAL_SMM_POSTS_COUNT in the backend

interface SMMStructuredViewProps {
  parentItem: Item;
  sectionName?: SectionName;
  tabName?: TabName;
}

export const SMMStructuredView: React.FC<SMMStructuredViewProps> = ({
  parentItem,
  sectionName = SectionName.SMM,
  tabName,
}) => {
  const { t } = useTranslation();
  const {
    items: ideas,
    loading,
    error,
  } = useListenStructuredItems(parentItem.campaign_id, parentItem.id);

  const [newlyGeneratedIds, setNewlyGeneratedIds] = useState(new Set<string>());
  const prevIdeasRef = useRef<StructuredItem[] | undefined>(undefined);

  const [isConfirmingGenerateAll, setIsConfirmingGenerateAll] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [generatingIdeaIds, setGeneratingIdeaIds] = useState(new Set<string>());
  const [errorGenerateAll, setErrorGenerateAll] = useState<string | null>(null);
  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const { userId } = useFirebaseLoginState();
  const state = useSelector((state: RootState) => state.appState);
  const selectedCampaignId = state.selectedCampaign?.id;
  const creditsLeft = state.subcription?.credits || 0;

  const [isConfirmingGenerateMore, setIsConfirmingGenerateMore] = useState(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [errorGenerateMore, setErrorGenerateMore] = useState<string | null>(null);
  const generateMoreCost = DEFAULT_GENERATION_COST;

  // Legacy Facebook items integration
  const { legacyItems, isDismissed, hasLegacyItems } = useLegacyFacebookItems();
  const [isLegacyPreviewOpen, setLegacyPreviewOpen] = useState(false);

  const hiddenIdeas = useMemo(
    () => ideas.filter((idea) => !idea.is_content_visible),
    [ideas]
  );
  const hiddenIdeasCount = hiddenIdeas.length;
  const generateAllCost = hiddenIdeasCount * DEFAULT_GENERATION_COST;

  const handleApiErrorCallback = useCallback((error: unknown) => {
    trackSentryError('Error revealing all items', { error });
    handleApiError({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setErrorGenerateAll,
      onSetErrorAlertOpen: (isOpen) => {
        if (!isOpen) setErrorGenerateAll(null);
      },
    });
  }, []);

  const handleGenerateMoreError = useCallback((error: unknown) => {
    trackSentryError('Error generating more items', { error });
    handleApiError({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setErrorGenerateMore,
      onSetErrorAlertOpen: (isOpen) => {
        if (!isOpen) setErrorGenerateMore(null);
      },
    });
  }, []);

  const handleGenerateMoreClick = () => {
    trackClickStructuredMoreIdeas(sectionName, tabName!);
    if (creditsLeft < generateMoreCost) {
      setUpgradePopupOpen(true);
    } else {
      setIsConfirmingGenerateMore(true);
    }
  };

  const handleConfirmGenerateMore = async () => {
    if (!userId || !selectedCampaignId) return;

    trackClickStructuredMoreIdeasConfirm(sectionName, tabName!);
    setIsConfirmingGenerateMore(false);
    setIsGeneratingMore(true);
    setErrorGenerateMore(null);

    try {
      await generateMoreIdeas(
        userId,
        selectedCampaignId,
        parentItem.id!,
        state.session,
        state.creativity
      );
    } catch (err) {
      trackSentryError('Error generating more items', { err });
      console.error('Error generating more items:', err);
      handleGenerateMoreError(err);
    } finally {
      setIsGeneratingMore(false);
    }
  };

  useEffect(() => {}, [parentItem.status, isGeneratingMore]);

  const handleGenerateAllClick = () => {
    if (hiddenIdeasCount > 0) {
      trackClickStructuredGenerateAll(sectionName, tabName!, generateAllCost);
      if (creditsLeft < generateAllCost) {
        setUpgradePopupOpen(true);
      } else {
        setIsConfirmingGenerateAll(true);
      }
    }
  };

  const handleConfirmGenerateAll = async () => {
    if (!userId || !selectedCampaignId || hiddenIdeasCount === 0) return;

    trackClickStructuredGenerateAllConfirm(sectionName, tabName!, generateAllCost);
    setIsConfirmingGenerateAll(false);
    setIsGeneratingAll(true);
    setErrorGenerateAll(null);

    setGeneratingIdeaIds(new Set(hiddenIdeas.map((idea) => idea.id!)));
    try {
      await revealStructuredItems(
        userId,
        selectedCampaignId,
        parentItem.id!,
        hiddenIdeas.map((idea) => idea.id!),
        state.session
      );
    } catch (err) {
      trackSentryError('Error revealing all items', { err });
      console.error('Error revealing all items:', err);
      handleApiErrorCallback(err);
    } finally {
      setIsGeneratingAll(false);
      setGeneratingIdeaIds(new Set<string>());
    }
  };

  useEffect(() => {
    const prevIdeas = prevIdeasRef.current || undefined;
    // Check if the previous state of ideas exists to compare against
    if (prevIdeas) {
      const justRevealedIds = new Set<string>();
      ideas.forEach((currentIdea) => {
        // Find the corresponding idea in the previous state
        const prevIdea = prevIdeas.find((p) => p.id === currentIdea.id);
        // If an idea was previously not visible and is now visible, it's newly generated
        if (
          prevIdea &&
          !prevIdea.is_content_visible &&
          currentIdea.is_content_visible
        ) {
          justRevealedIds.add(currentIdea.id!);
        }
      });

      // If we found any newly revealed items, add them to our state
      if (justRevealedIds.size > 0) {
        setNewlyGeneratedIds((prev) => new Set([...prev, ...justRevealedIds]));
      }
    }
    // Store the current ideas array for the next render comparison
    prevIdeasRef.current = ideas;
  }, [ideas]);

  if (
    (loading && ideas.length === 0) ||
    (parentItem.status === LoadingStatus.LOADING && ideas.length === 0)
  ) {
    return <GenerationInProgressMessage />;
  }

  if (error) {
    return (
      <Typography color="error">Error loading post ideas: {error.message}</Typography>
    );
  }

  return (
    <Grid container spacing={5}>
      <Grid size={12}>
        <Stack spacing={5}>
          {/* Legacy Facebook items banner */}
          {hasLegacyItems && !isDismissed && selectedCampaignId && (
            <LegacyPreviewBanner
              campaignId={selectedCampaignId}
              onOpen={() => setLegacyPreviewOpen(true)}
              legacyItems={legacyItems}
            />
          )}
          <AnimatePresence initial={false}>
            {ideas.map((idea) => (
              <motion.div
                key={idea.id}
                layout="position" // smooth animation without scaling distortion
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                {idea.is_content_visible ? (
                  <GeneratedPost
                    post={idea}
                    isNew={newlyGeneratedIds.has(idea.id!)}
                    sectionName={sectionName}
                    tabName={tabName}
                  />
                ) : (
                  <IdeaCard
                    idea={idea}
                    parentItemId={parentItem.id!}
                    isGenerating={generatingIdeaIds.has(idea.id!)}
                    sectionName={sectionName}
                    tabName={tabName}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </Stack>
      </Grid>

      <Grid size={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        {hiddenIdeasCount !== 0 && (
          <>
            {isGeneratingAll ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2 }}>
                <GradientCircularProgress size={16} />
                <Typography
                  sx={{
                    color: colors.text.hint,
                    fontWeight: 'medium',
                    lineHeight: '24.5px',
                  }}
                >
                  {t('app.structuredItems.actions.generatePost.progressMessage')}
                </Typography>
              </Stack>
            ) : (
              <GradientButton
                buttonText={t(
                  'app.structuredItems.actions.generateAllPosts.buttonText'
                )}
                onSubmitButtonHandler={handleGenerateAllClick}
                iconLeft={<IconRocketLaunchFilled />}
                isLoading={isGeneratingAll}
              />
            )}
          </>
        )}
        {isGeneratingMore ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 2 }}>
            <GradientCircularProgress size={16} />
            <Typography
              sx={{
                color: colors.text.hint,
                fontWeight: 'medium',
                lineHeight: '24.5px',
              }}
            >
              {t('app.structuredItems.actions.generateMore.generatingMessage')}
            </Typography>
          </Stack>
        ) : (
          <GradientButton
            version="text"
            buttonText={t('app.structuredItems.actions.generateMore.buttonText')}
            onSubmitButtonHandler={handleGenerateMoreClick}
            iconLeft={<IconGenerateMore />}
          />
        )}
      </Grid>

      <ConfirmationPopup
        open={isConfirmingGenerateAll}
        onClose={() => {
          trackClickStructuredGenerateAllCancel(sectionName, tabName!, generateAllCost);
          setIsConfirmingGenerateAll(false);
        }}
        onSubmit={handleConfirmGenerateAll}
        title={t('app.structuredItems.actions.generateAllPosts.confirmationTitle', {
          count: hiddenIdeasCount,
        })}
        message={t('app.structuredItems.actions.generateAllPosts.confirmationMessage', {
          count: hiddenIdeasCount,
          cost: generateAllCost,
        })}
        confirmButtonText={t('app.popups.generate.buttonText', {
          count: generateAllCost,
        })}
        showRocketButton={true}
      />
      <ConfirmationPopup
        open={isConfirmingGenerateMore}
        onClose={() => {
          trackClickStructuredMoreIdeasCancel(sectionName, tabName!);
          setIsConfirmingGenerateMore(false);
        }}
        onSubmit={handleConfirmGenerateMore}
        title={t('app.structuredItems.actions.generateMore.confirmationTitle')}
        message={t('app.structuredItems.actions.generateMore.confirmationMessage', {
          count: additionalIdeasCount,
        })}
        confirmButtonText={t('app.popups.generate.buttonText', {
          count: generateMoreCost,
        })}
        showRocketButton={true}
      />
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleApiErrorCallback}
      />
      <AlertPopup
        open={!!errorGenerateAll}
        onClose={() => setErrorGenerateAll(null)}
        onSubmit={() => setErrorGenerateAll(null)}
        alertText={errorGenerateAll || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
      <AlertPopup
        open={!!errorGenerateMore}
        onClose={() => setErrorGenerateMore(null)}
        onSubmit={() => setErrorGenerateMore(null)}
        alertText={errorGenerateMore || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
      <LegacyCampaignPreviewPopup
        open={isLegacyPreviewOpen}
        onClose={() => setLegacyPreviewOpen(false)}
        items={legacyItems}
      />
    </Grid>
  );
};
